export type DoubtBuddyStep = { title: string; content: string };

export type DoubtBuddyExtractedSection = {
  heading: string;
  content?: string;
  steps?: DoubtBuddyStep[];
};

export type DoubtBuddyRagSolution = {
  hints: string[];
  steps: string[];
  answer: string;
};

function normalizeBuddyAnswerForLegacyParse(text: string): string {
  let n = String(text).replace(/\r\n/g, "\n");
  n = n.replace(/([^\n])(####\s*Step\s*\d)/gi, "$1\n$2");
  n = n.replace(/(?<=[^#\n])###(?!#)/g, "\n###");
  n = n.replace(/^###\s*(Step\s*\d)/gim, "#### $1");
  n = n.replace(/(^|\n)###(?!#)(?!\s)/g, "$1### ");
  n = n.replace(/####Step(\s*\d)/gi, "#### Step $1");
  n = n.replace(/####\s*Step\s*(\d+)/gi, "#### Step $1");
  return n;
}

function splitGluedBuddyHeadingBody(rest: string): {
  heading: string;
  bodyPrefix: string;
} {
  const r = (rest || "").trim();
  const fm = r.match(/^(Final Solution)(.*)$/i);
  if (fm && fm[2] != null && String(fm[2]).trim().length > 0) {
    return { heading: fm[1].trim(), bodyPrefix: fm[2] };
  }
  let m = r.match(/^Understanding the Problem(The[\s\S]*)$/i);
  if (m) {
    return { heading: "Understanding the Problem", bodyPrefix: m[1] };
  }
  m = r.match(/^Finding a Suitable Method(To[\s\S]*)$/i);
  if (m) {
    return { heading: "Finding a Suitable Method", bodyPrefix: m[1] };
  }
  m = r.match(/^Breaking the Solution into Steps([\s\S]*)$/i);
  if (m) {
    return {
      heading: "Breaking the Solution into Steps",
      bodyPrefix: (m[1] || "").trimStart(),
    };
  }
  return { heading: r, bodyPrefix: "" };
}

function extractSectionsInternal(text: string): FinalSlots {
  if (text == null || String(text).trim() === "") {
    return removeDuplicateEntries([]);
  }
  const normalized = normalizeBuddyAnswerForLegacyParse(text);
  const lines = normalized.split("\n");
  const sections: DoubtBuddyExtractedSection[] = [];
  let currentHeading = "";
  let currentContent = "";
  let currentSteps: DoubtBuddyStep[] = [];
  let currentStepTitle = "";
  let isInStep = false;

  lines.forEach((line) => {
    const trimmed = line.trimStart();
    if (trimmed.startsWith("### ")) {
      if (currentHeading) {
        if (isInStep && currentStepTitle) {
          currentSteps.push({
            title: currentStepTitle,
            content: currentContent.trim(),
          });
        }
        sections.push({
          heading: currentHeading.trim(),
          steps: currentSteps.length ? currentSteps : undefined,
          content: currentSteps.length ? undefined : currentContent.trim(),
        });
      }
      const rest = trimmed.substring(4).trimStart();
      const split = splitGluedBuddyHeadingBody(rest);
      currentHeading = split.heading;
      currentContent = split.bodyPrefix ? `${split.bodyPrefix}\n` : "";
      currentSteps = [];
      currentStepTitle = "";
      isInStep = false;
    } else if (/^####\s+Step\s*\d/i.test(trimmed)) {
      const sm = trimmed.match(/^####\s+Step\s*(\d+)\s*[:-]?\s*(.*)$/i);
      if (!sm) {
        currentContent += `${line}\n`;
        return;
      }
      const isFirstStepInSection = !currentStepTitle;
      if (currentStepTitle) {
        currentSteps.push({
          title: currentStepTitle.trim(),
          content: currentContent.trim(),
        });
      }
      const introBeforeFirstStep = isFirstStepInSection
        ? currentContent.replace(/\s+$/, "")
        : "";
      currentStepTitle = `#### Step ${sm[1]}`;
      const fromHeadingLine =
        sm[2] && String(sm[2]).trim().length
          ? `${String(sm[2]).replace(/\s+$/, "")}\n`
          : "";
      currentContent =
        (introBeforeFirstStep.length ? `${introBeforeFirstStep}\n\n` : "") +
        fromHeadingLine;
      isInStep = true;
    } else {
      currentContent += `${line}\n`;
    }
  });

  if (currentHeading) {
    if (isInStep && currentStepTitle) {
      currentSteps.push({
        title: currentStepTitle,
        content: currentContent.trim(),
      });
    }
    sections.push({
      heading: currentHeading.trim(),
      steps: currentSteps.length ? currentSteps : undefined,
      content: currentSteps.length ? undefined : currentContent.trim(),
    });
  }

  if (sections.length === 0 && String(text).trim().length > 0) {
    sections.push({
      heading: "Let's Understand",
      content: String(text).trim(),
      steps: undefined,
    });
  }

  return removeDuplicateEntries(sections);
}

type FinalSlots = [
  DoubtBuddyExtractedSection | Record<string, never>, // 0: understand
  Record<string, never>, // 1: unused
  DoubtBuddyExtractedSection | Record<string, never>, // 2: steps
  DoubtBuddyExtractedSection | Record<string, never>, // 3: final
];

function removeDuplicateEntries(
  answer: DoubtBuddyExtractedSection[],
): FinalSlots {
  const finalAnswer: FinalSlots = [{}, {}, {}, {}];
  if (answer?.length) {
    answer.forEach((element) => {
      const isFinal = element?.heading === "Final Solution";

      if (element?.heading && element?.content && !element?.steps && !isFinal) {
        const slot0 = finalAnswer[0] as DoubtBuddyExtractedSection;
        if (slot0?.content && String(slot0.content).trim().length > 0) {
          const prev = String(slot0.content).trimEnd();
          const next = String(element.content).trimStart();
          const sep =
            prev.endsWith("\n") || next.startsWith("\n") ? "\n" : "\n\n";
          finalAnswer[0] = { ...slot0, content: `${prev}${sep}${next}` };
        } else {
          finalAnswer[0] = element;
        }
      } else if (
        element?.heading &&
        !element?.content &&
        element?.steps &&
        element.steps.length &&
        !isFinal
      ) {
        finalAnswer[2] = element;
      } else if (
        element?.heading &&
        element?.content &&
        !element?.steps &&
        isFinal
      ) {
        finalAnswer[3] = element;
      }
    });
  }

  const block = finalAnswer[2] as DoubtBuddyExtractedSection;
  if (block?.steps?.length) {
    const ordered: DoubtBuddyStep[] = [];
    for (let i = 0; i < block.steps.length; i++) {
      const current = block.steps[i];
      const m = current?.title?.match(/####\s*Step\s*(\d+)/i);
      if (m) {
        const n = Number.parseInt(m[1], 10);
        if (n > 0) {
          ordered[n - 1] = current;
        }
      }
    }
    const orderedSteps = ordered.filter(Boolean);
    if (orderedSteps.length) {
      block.steps = orderedSteps;
    }
  }

  return finalAnswer;
}

/** Returns raw extracted sections (for debugging / alternate renderers). */
export function extractSections(text: string): DoubtBuddyExtractedSection[] {
  const slots = extractSectionsInternal(text);
  const result: DoubtBuddyExtractedSection[] = [];
  for (const slot of slots) {
    const s = slot as DoubtBuddyExtractedSection;
    if (s != null && typeof s.heading === "string" && s.heading.length > 0) {
      result.push(s);
    }
  }
  return result;
}

/**
 * When section parsing misses `### Final Solution` (streaming/glue quirks), recover the
 * trailing answer so UI can show "Give me the Solution" and the final panel.
 */
export function extractFallbackFinalAnswer(text: string): string {
  const n = String(text).replace(/\r\n/g, "\n").trim();
  if (!n) return "";
  const afterHeading = n.match(/###\s*Final Solution[^\n]*\r?\n([\s\S]*)$/im);
  if (afterHeading?.[1]) {
    const body = afterHeading[1].trim();
    if (body.length > 0) return body;
  }
  const glued = n.match(/###\s*Final Solution\s+([\s\S]+)$/im);
  if (glued?.[1]) {
    const body = glued[1].trim();
    if (body.length > 0) return body;
  }
  return "";
}

/**
 * Adapt DoubtBuddy output to the existing RAG panel shape:
 *   hints[0]  = "Let's Understand" content (slot 0 body)
 *   steps[]   = body of each `#### Step N` in slot 2 (prefix stripped)
 *   answer    = slot 3 (Final Solution) content
 */
export function extractToRagSolution(text: string): DoubtBuddyRagSolution {
  const sections = extractSections(text);
  const slot0 = sections.find(
    (s) => !!s.content && !s.steps && s.heading !== "Final Solution",
  );
  const slot2 = sections.find(
    (s) => !!s.steps && s.steps.length > 0 && s.heading !== "Final Solution",
  );
  const slot3 = sections.find(
    (s) => !!s.content && !s.steps && s.heading === "Final Solution",
  );

  const understandBody = slot0?.content?.trim() ?? "";
  const stepBodies =
    slot2?.steps?.map((step) => {
      const withoutPrefix = step.content.replace(
        /^####\s*Step\s*\d+\s*[:-]?\s*/i,
        "",
      );
      return withoutPrefix.trim();
    }) ?? [];
  let answerBody = slot3?.content?.trim() ?? "";
  if (!answerBody) {
    answerBody = extractFallbackFinalAnswer(text);
  }

  return {
    hints: understandBody ? [understandBody] : [],
    steps: stepBodies.filter((s) => s.length > 0),
    answer: answerBody,
  };
}
